import * as React from "react";
import { useNavigate } from "react-router-dom";
import { type ColumnDef } from "@tanstack/react-table";

import { DataTable } from "@/components/ui/data-table";
import type { Program } from "@/types/program";
import type { StatValue } from "@/types/dashboard";
import { formatNumber } from "@/utils/formatters";
import { DashboardCard, StatusText } from "@/components/dashboard/dashboard-card";
import { programDisplayName } from "./helpers";

type ProgramRow = Program & { affiliateCount?: number };

const columns: ColumnDef<ProgramRow>[] = [
  {
    accessorKey: "name",
    header: "Program",
    meta: { width: "w-56" },
    cell: ({ row }) => (
      <span className="truncate font-medium text-foreground">
        {programDisplayName(row.original)}
      </span>
    ),
  },
  {
    accessorKey: "commissionValue",
    header: "Commission",
    meta: { width: "w-28" },
    cell: ({ row }) => (
      <span className="tabular-nums text-foreground">
        {Number(row.original.commissionValue)}
        {(row.original.commissionType as string) === "FIXED" ? " USD" : "%"}
      </span>
    ),
  },
  {
    accessorKey: "discountValue",
    header: "Discount",
    meta: { width: "w-24" },
    cell: ({ row }) => (
      <span className="tabular-nums text-foreground">
        {Number(row.original.discountValue)}%
      </span>
    ),
  },
  {
    accessorKey: "attributionMethod",
    header: "Tracking",
    meta: { width: "w-24" },
    cell: ({ row }) => (
      <span className="text-foreground">
        {row.original.attributionMethod === "SNEAKYLINK" ? "Link" : "Code"}
      </span>
    ),
  },
  {
    accessorKey: "affiliateCount",
    header: "Affiliates",
    meta: { width: "w-24" },
    cell: ({ row }) => (
      <span className="tabular-nums text-foreground">
        {typeof row.original.affiliateCount === "number"
          ? formatNumber(row.original.affiliateCount)
          : "—"}
      </span>
    ),
  },
  {
    accessorKey: "status",
    header: "Status",
    meta: { width: "w-28" },
    cell: ({ row }) => <StatusText status={row.original.status} />,
  },
];

export function ProgramsCard({
  programs,
  affiliateCounts,
  stats,
  isLoading,
}: {
  programs: Program[] | undefined;
  affiliateCounts: Record<string, number> | undefined;
  stats?: StatValue[];
  isLoading: boolean;
}) {
  const navigate = useNavigate();
  const rows = React.useMemo<ProgramRow[]>(
    () =>
      (programs ?? []).map((program) => ({
        ...program,
        affiliateCount: affiliateCounts?.[program.id],
      })),
    [programs, affiliateCounts]
  );

  return (
    <DashboardCard
      id="dashboard-programs-card"
      title="Programs"
      to="/brand/programs"
      fill
      stats={stats}
      bodyClassName="px-3 pb-3"
    >
      {/* fill + lg:h-full: on the dashboard this card borrows the network
          card's row height and scrolls inside when there are more programs. */}
      <DataTable
        columns={columns}
        data={rows}
        isLoading={isLoading}
        skeletonRows={2}
        getRowId={(row) => row.id}
        onRowClick={(row) => navigate(`/brand/programs/${row.id}`)}
        emptyText="No programs yet"
        maxHeight="max-h-[26rem] lg:max-h-full"
        className="lg:h-full"
      />
    </DashboardCard>
  );
}
